import { ReReviewBadge, AutoBadge, VerifierBreakdown, parseVerifierResults, type VerifierResultsCounts } from './VerifierBadge.js';

// Phase 5 of verifier-mode rollout adds three optional artifact fields. They
// flow through ingest-ci-artifact as top-level row attributes (matching the
// pattern already used for verdict_state, duration_ms, etc.) — the portal
// reads them with the same defensive type-guards as the rest of ArtifactLike.
//
// review_kind: 'initial' for the first DV review on a PR; 're-review' on
//   every subsequent run. Surfaces as a small RE-REVIEW badge so reviewers
//   can tell at a glance which run on a noisy PR was the verifier pass.
// trigger: 'user-initiated' (slash-command, today's only path) or
//   'auto-triggered' (reserved for a future push-on-PR auto-dispatch).
//   Renders as an AUTO badge when set to auto-triggered; default suppressed.
// verifier_results: per-finding classification summary, present iff the run
//   was a verifier-mode pass. Renders as a compact 4-icon chip:
//     N ✅  resolved
//     N ⚠️  unresolved
//     N ◐   partial
//     N ↩   withdrawn
//   The chip is the most-condensed signal for "this re-review ran in
//   verifier mode and these are its outcomes". The full per-finding
//   rationales live in the council body (rendered upstream by the runner).
interface ArtifactLike {
  ts?: unknown;
  verdict_state?: unknown;
  mode?: unknown;
  cost_usd?: unknown;
  cost_tokens_in?: unknown;
  cost_tokens_out?: unknown;
  duration_ms?: unknown;
  review_kind?: unknown;
  trigger?: unknown;
  verifier_results?: unknown;
}

interface RunRow {
  ts: string;
  verdictState: string | null;
  mode: string | null;
  costUsd: number | null;
  tokensIn: number | null;
  tokensOut: number | null;
  durationMs: number | null;
  reviewKind: string | null;
  trigger: string | null;
  verifierResults: VerifierResultsCounts | null;
}

// Exported for unit tests. Not part of the component's public surface.
export function buildRunRows(artifacts: ArtifactLike[]): RunRow[] {
  return [...artifacts]
    .filter((a) => typeof a.ts === 'string')
    .sort((a, b) => (a.ts as string).localeCompare(b.ts as string))
    .map((a) => ({
      ts: a.ts as string,
      verdictState: typeof a.verdict_state === 'string' ? a.verdict_state : null,
      mode: typeof a.mode === 'string' ? a.mode : null,
      costUsd: typeof a.cost_usd === 'number' ? a.cost_usd : null,
      tokensIn: typeof a.cost_tokens_in === 'number' ? a.cost_tokens_in : null,
      tokensOut: typeof a.cost_tokens_out === 'number' ? a.cost_tokens_out : null,
      durationMs: typeof a.duration_ms === 'number' && Number.isFinite(a.duration_ms) && a.duration_ms >= 0 ? a.duration_ms : null,
      reviewKind: typeof a.review_kind === 'string' ? a.review_kind : null,
      trigger: typeof a.trigger === 'string' ? a.trigger : null,
      verifierResults: parseVerifierResults(a.verifier_results),
    }));
}

// Exported for unit tests. Renders milliseconds as one of:
//   "47s"     for sub-minute runs (no fractional seconds — adds noise)
//   "2m 37s"  for sub-hour runs (minute+second is the natural reading unit)
//   "1h 5m"   for the rare runs over an hour (drops seconds — would be noise
//             at this scale, and a council run that took an hour is its own
//             slow-reviewer triage signal anyway)
//
// Defensive: returns "—" for null, NaN, Infinity, or negative input. The
// schema and buildRunRows already filter these, but a future caller (or a
// hand-typed test fixture) shouldn't be able to make this function emit
// "Infinityh NaNm".
export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function fmtUsd(n: number | null): string {
  if (n === null) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtTimestamp(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
}

function fmtTokens(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString();
}

export function CouncilRunsTable({ artifacts }: { artifacts: ArtifactLike[] }) {
  const rows = buildRunRows(artifacts);

  if (rows.length === 0) {
    return <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>No council runs yet.</p>;
  }

  // Right-aligned columns: Cost, Tokens, Duration. Indices 4..6. Verifier
  // column at index 7 is left-aligned — the 4-icon chip reads as a single
  // unit and right-aligning it would visually compete with the numeric
  // columns to its left.
  const headers = ['Run', 'When', 'Verdict', 'Mode', 'Cost', 'Tokens (in / out)', 'Duration', 'Verifier'];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
        <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
          <tr>
            {headers.map((h, i) => (
              <th
                key={h}
                style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--loop-neutral-700)', fontWeight: 528, textAlign: i >= 4 && i <= 6 ? 'right' : 'left' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.ts} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
              <td style={{ padding: '6px 12px', fontWeight: 528 }}>{i + 1}</td>
              <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{fmtTimestamp(r.ts)}</td>
              <td style={{ padding: '6px 12px' }}>
                {r.verdictState ? <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.verdictState}</span> : <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>}
              </td>
              <td style={{ padding: '6px 12px' }}>
                {r.mode ? <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.mode}</span> : <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>}
                {r.reviewKind === 're-review' && <ReReviewBadge />}
                {r.trigger === 'auto-triggered' && <AutoBadge />}
              </td>
              <td style={{ padding: '6px 12px', textAlign: 'right' }}>{fmtUsd(r.costUsd)}</td>
              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {r.tokensIn !== null || r.tokensOut !== null
                  ? `${fmtTokens(r.tokensIn)} / ${fmtTokens(r.tokensOut)}`
                  : '—'}
              </td>
              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.75rem' }}>{formatDuration(r.durationMs)}</td>
              <td style={{ padding: '6px 12px' }}>
                {r.verifierResults
                  ? <VerifierBreakdown counts={r.verifierResults} />
                  : <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
